import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class NavigationHistoryController extends Controller {
  static values = { fallback: { type: String, default: "/" } };

  connect() {
    this.record = this.record.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);

    document.addEventListener("turbo:load", this.record);
    globalThis.addEventListener("pageshow", this.handlePageShow);
    this.record();
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.record);
    globalThis.removeEventListener("pageshow", this.handlePageShow);
  }

  handlePageShow(event) {
    const currentUrl = new URL(globalThis.location.href);
    const redirectUrl = sessionStorage.getItem('backHistoryRedirect');

    const navigationType = performance.getEntriesByType('navigation')[0]?.type;
    const isHistoryRestore =  event.persisted || navigationType === 'back_forward';

    if (!isHistoryRestore) return;

    if (!redirectUrl) return;

    const targetUrl = new URL(redirectUrl, globalThis.location.origin);

    if (currentUrl.href !== targetUrl.href) return;

    sessionStorage.removeItem('backHistoryRedirect');
    globalThis.history.back();
  }

  back(event) {
    event?.preventDefault();

    if (globalThis.history.length > 1) {
      // Let Turbo handle popstate as a restoration visit, preserving its
      // cached page and scroll position instead of replacing the history entry.
      globalThis.history.back();
      return;
    }

    this.visit(this.fallbackUrl(event));
  }

  record() {
    const currentUrl = this.internalUrl(globalThis.location.href);
    if (!currentUrl) return;

    const history = this.history;
    if (history.at(-1) === currentUrl) return;

    this.history = [...history, currentUrl].slice(-100);
  }

  fallbackUrl(event) {
    const requestedFallback = event?.currentTarget?.getAttribute("href") || this.fallbackValue;
    return this.internalUrl(requestedFallback) || new URL("/", globalThis.location.origin).href;
  }

  internalUrl(value) {
    if (!value) return null;

    try {
      const url = new URL(value, globalThis.location.origin);
      return url.origin === globalThis.location.origin ? url.href : null;
    } catch {
      return null;
    }
  }

  visit(url) {
    Turbo.visit(url, { action: "replace" });
  }

  addReturnTo(event) {
    const modal = document.getElementById("scroll_modal_content");
    const modalElement = modal?.closest(".modal");
    const link = event.currentTarget;

    const url = new URL(link.href, globalThis.location.origin);

    if (url.origin !== globalThis.location.origin) return;

    // Remove any stale return URL first.
    url.searchParams.delete("return_to");

    if (!modalElement?.classList.contains("show")) {
      link.href = url.href;
      return;
    }

    const modalContent = modal.querySelector("[data-modal-return-url]");
    const rawReturnTo = modalContent?.dataset.modalReturnUrl?.trim();

    const returnTo = rawReturnTo?.replace(/^['"]|['"]$/g, "");

    if (!returnTo) {
      link.href = url.href;
      return;
    }

    const returnUrl = new URL(returnTo, globalThis.location.origin);

    // A hover-card endpoint isn't a valid modal return destination.
    if (
      returnUrl.origin !== globalThis.location.origin ||
      returnUrl.pathname.includes("/user_hover_card")
    ) {
      link.href = url.href;
      return;
    }

    url.searchParams.set("return_to", returnUrl.pathname + returnUrl.search);
    link.href = url.href;
  }

  get history() {
    try {
      const history = JSON.parse(globalThis.sessionStorage.getItem(this.storageKey) || "[]");
      return Array.isArray(history) ? history.filter((url) => this.internalUrl(url)) : [];
    } catch {
      return [];
    }
  }

  set history(history) {
    try {
      globalThis.sessionStorage.setItem(this.storageKey, JSON.stringify(history));
    } catch {
      // Navigation still works with the internal fallback when storage is unavailable.
    }
  }

  get storageKey() {
    return "itoons.navigationHistory";
  }
}
